import { create } from 'zustand';

/**
 * Ephemeral selection state for the affiliate invitations table bulk actions.
 *
 * Mirrors the affiliate table pattern (a Set of ids + page-level select-all) and
 * adds the "select everything matching the current filter" affordance: when the
 * whole page is checked, the user can promote the selection to the full filter.
 * Any manual toggle drops back to explicit-id selection.
 */
interface InvitationSelectionStore {
  selectedIds: Set<string>;
  /** True when the user opted to act on every invitation matching the filter. */
  selectAllMatching: boolean;
  /** Total invitations matching the current filter (from the list meta). */
  matchingTotal: number;

  toggleSelection: (id: string) => void;
  toggleSelectAll: (ids: string[]) => void;
  enableSelectAllMatching: (total: number) => void;
  setMatchingTotal: (total: number) => void;
  clearSelection: () => void;
}

const useInvitationSelectionStore = create<InvitationSelectionStore>((set, get) => ({
  selectedIds: new Set<string>(),
  selectAllMatching: false,
  matchingTotal: 0,

  toggleSelection: (id) => {
    const next = new Set(get().selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    // A manual change is an explicit-id selection: leave the filter-wide mode.
    set({ selectedIds: next, selectAllMatching: false });
  },

  toggleSelectAll: (ids) => {
    const { selectedIds } = get();
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
    const next = new Set(selectedIds);
    if (allSelected) {
      for (const id of ids) next.delete(id);
    } else {
      for (const id of ids) next.add(id);
    }
    set({ selectedIds: next, selectAllMatching: false });
  },

  enableSelectAllMatching: (total) => {
    set({ selectAllMatching: true, matchingTotal: total });
  },

  setMatchingTotal: (total) => {
    set({ matchingTotal: total });
  },

  clearSelection: () => {
    set({ selectedIds: new Set(), selectAllMatching: false, matchingTotal: 0 });
  },
}));

export default useInvitationSelectionStore;
